import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { 
  ArrowLeft, 
  ArrowRight,
  AlertTriangle,
  Play,
  FileText,
  Clock,
  Shield,
  DollarSign,
  Lock,
  Zap,
  CheckCircle,
  Star
} from 'lucide-react';
import CheckoutModal from '../components/landing/CheckoutModal';
import { usePrices } from '../context/PricesContext';

const CriticalMistakesPage = () => {
  const [checkoutOpen, setCheckoutOpen] = useState(false);
  const { getPrice, getOldPrice } = usePrices();
  const price = getPrice('critical_mistakes');
  const oldPrice = getOldPrice('critical_mistakes');

  const mistakes = [
    {
      title: 'Ошибки при выборе поставщика',
      desc: 'Как распознать ненадёжного поставщика до первой сделки',
      icon: Shield,
    },
    {
      title: 'Проблемы с документами',
      desc: 'Какие документы должны быть и как их проверять',
      icon: FileText,
    },
    {
      title: 'Ошибки ценообразования',
      desc: 'Как не продавать в минус и считать реальную маржу',
      icon: DollarSign,
    },
    {
      title: 'Нарушения правил Amazon',
      desc: 'Что приводит к блокировке аккаунта',
      icon: Lock,
    },
    {
      title: 'Логистические ошибки',
      desc: 'Как не потерять товар и деньги на доставке',
      icon: AlertTriangle,
    },
  ];

  return (
    <div className="min-h-screen bg-[#08080A]">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-black/90 backdrop-blur-xl border-b border-zinc-800/50">
        <div className="max-w-6xl mx-auto px-4 h-16 flex items-center justify-between">
          <Link 
            to="/catalog" 
            className="flex items-center gap-2 text-zinc-400 hover:text-white transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            <span className="text-sm font-medium">Каталог</span>
          </Link>
          
          <button
            onClick={() => setCheckoutOpen(true)}
            className="px-4 py-2 rounded-lg bg-gradient-to-r from-rose-500 to-orange-500 text-white font-bold text-sm hover:from-rose-400 hover:to-orange-400 transition-all"
          >
            Получить за ${price}
          </button>
        </div>
      </header>

      {/* Hero Section */}
      <section className="pt-24 pb-12 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-rose-500/20 border border-rose-500/30 text-rose-400 font-bold text-sm mb-6">
            <AlertTriangle className="w-4 h-4" />
            <span>МИНИ-КУРС</span>
          </div>
          
          <h1 className="text-4xl sm:text-5xl md:text-6xl font-black text-white mb-6 leading-tight">
            Критические ошибки
            <span className="block text-rose-400">на Amazon Wholesale</span>
          </h1>
          
          <p className="text-xl text-zinc-300 mb-8 max-w-2xl mx-auto">
            Опыт, который обычно обходится в <span className="text-white font-bold">тысячи долларов</span> потерь и блокировок
          </p>

          <div className="flex flex-wrap items-center justify-center gap-4 mb-10">
            <div className="flex items-center gap-2 px-4 py-2 rounded-xl bg-zinc-800/50 border border-zinc-700/50">
              <Play className="w-5 h-5 text-rose-400" />
              <span className="text-white font-medium">30 мин видео</span>
            </div>
            <div className="flex items-center gap-2 px-4 py-2 rounded-xl bg-zinc-800/50 border border-zinc-700/50">
              <FileText className="w-5 h-5 text-emerald-400" />
              <span className="text-white font-medium">Чек-лист</span>
            </div>
            <div className="flex items-center gap-2 px-4 py-2 rounded-xl bg-zinc-800/50 border border-zinc-700/50">
              <Clock className="w-5 h-5 text-blue-400" />
              <span className="text-white font-medium">Доступ навсегда</span>
            </div>
          </div>

          {/* Price Card */}
          <div className="max-w-sm mx-auto p-6 rounded-2xl bg-gradient-to-b from-zinc-800/50 to-zinc-900/50 border-2 border-rose-500/30">
            <div className="flex items-baseline justify-center gap-3 mb-2">
              <span className="text-zinc-500 line-through text-xl">$165</span>
              <span className="text-5xl font-black text-white">${price}</span>
            </div>
            <p className="text-rose-400 font-bold mb-4">Скидка 40%</p>
            
            <button
              onClick={() => setCheckoutOpen(true)}
              className="w-full py-4 px-6 rounded-xl bg-gradient-to-r from-rose-500 to-orange-500 text-white font-black text-lg hover:from-rose-400 hover:to-orange-400 transition-all duration-300 flex items-center justify-center gap-2 shadow-lg shadow-rose-500/25"
            >
              <span>Получить мини-курс</span>
              <ArrowRight className="w-5 h-5" />
            </button>
          </div>
        </div>
      </section>

      {/* What's Inside */}
      <section className="py-16 px-4 bg-zinc-900/30">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl sm:text-4xl font-black text-white text-center mb-10">
            Что внутри мини-курса
          </h2>
          
          <div className="grid sm:grid-cols-2 gap-4">
            {mistakes.map((item, i) => {
              const Icon = item.icon;
              return (
                <div key={i} className="p-5 rounded-2xl bg-zinc-800/50 border border-zinc-700/40 hover:border-rose-500/30 transition-colors">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-xl bg-rose-500/20 flex items-center justify-center flex-shrink-0">
                      <Icon className="w-6 h-6 text-rose-400" />
                    </div>
                    <div>
                      <h3 className="text-lg font-bold text-white mb-1">{item.title}</h3>
                      <p className="text-zinc-400 text-sm">{item.desc}</p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Why Important */}
      <section className="py-16 px-4">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl sm:text-4xl font-black text-white text-center mb-4">
            Почему это важно
          </h2>
          <p className="text-zinc-400 text-center text-lg mb-10">
            Одна ошибка может стоить вам всего бизнеса
          </p>
          
          <div className="grid sm:grid-cols-3 gap-4 mb-10">
            {[
              { icon: Lock, text: 'Блокировка аккаунта', color: 'rose' },
              { icon: AlertTriangle, text: 'Потеря товара', color: 'amber' },
              { icon: DollarSign, text: 'Потеря денег', color: 'red' },
            ].map((item, i) => (
              <div key={i} className="p-5 rounded-2xl bg-rose-500/10 border border-rose-500/20 text-center">
                <item.icon className="w-10 h-10 text-rose-400 mx-auto mb-3" />
                <p className="text-white font-bold">{item.text}</p>
              </div>
            ))}
          </div>

          <div className="text-center p-8 rounded-2xl bg-gradient-to-br from-zinc-800/50 to-zinc-900/50 border border-zinc-700/30">
            <p className="text-zinc-300 text-lg mb-3">
              Вы получаете чужой опыт, который обычно приобретается через
            </p>
            <p className="text-rose-400 font-black text-2xl mb-4">
              потери и блокировки
            </p>
            <p className="text-white font-bold text-lg">
              Стоимость таких ошибок всегда выше цены этого курса.
            </p>
          </div>
        </div>
      </section>

      {/* Reviews */}
      <section className="py-16 px-4 bg-zinc-900/30">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl font-black text-white text-center mb-10">
            Отзывы
          </h2>
          
          <div className="grid sm:grid-cols-2 gap-4">
            {[
              { name: 'Алексей', text: 'Пожалел что не прошёл этот курс раньше. Сэкономил бы минимум $2000 на ошибках.' },
              { name: 'Марина', text: 'Очень конкретно и по делу. Никакой воды, только практика.' },
            ].map((review, i) => (
              <div key={i} className="p-5 rounded-2xl bg-zinc-800/50 border border-zinc-700/40">
                <div className="flex items-center gap-1 mb-3">
                  {[...Array(5)].map((_, j) => (
                    <Star key={j} className="w-4 h-4 text-amber-400 fill-amber-400" />
                  ))}
                </div>
                <p className="text-zinc-300 mb-3">"{review.text}"</p>
                <p className="text-white font-bold">— {review.name}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20 px-4">
        <div className="max-w-2xl mx-auto text-center">
          <h2 className="text-3xl sm:text-4xl font-black text-white mb-6">
            Не повторяйте чужих ошибок
          </h2>
          <p className="text-zinc-300 text-lg mb-8">
            Получите проверенный опыт за <span className="text-rose-400 font-bold">${price}</span> вместо тысяч долларов потерь
          </p>
          
          <button
            onClick={() => setCheckoutOpen(true)}
            className="px-10 py-5 rounded-xl bg-gradient-to-r from-rose-500 to-orange-500 text-white font-black text-xl hover:from-rose-400 hover:to-orange-400 transition-all duration-300 flex items-center justify-center gap-3 mx-auto shadow-lg shadow-rose-500/25"
          >
            <span>Получить мини-курс за ${price}</span>
            <ArrowRight className="w-6 h-6" />
          </button>
          
          <div className="flex items-center justify-center gap-6 mt-6 text-sm text-zinc-500">
            <span className="flex items-center gap-1.5">
              <CheckCircle className="w-4 h-4 text-emerald-500" />
              Доступ навсегда
            </span>
            <span className="flex items-center gap-1.5">
              <Shield className="w-4 h-4 text-blue-500" />
              Безопасная оплата
            </span>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-6 px-4 border-t border-zinc-800/50">
        <div className="max-w-6xl mx-auto flex items-center justify-between">
          <span className="text-zinc-600 text-sm">© 2025 Вадим Малюк</span>
          <Link to="/catalog" className="text-zinc-500 hover:text-white text-sm">
            Вернуться в каталог
          </Link>
        </div>
      </footer>

      {/* Checkout Modal */}
      <CheckoutModal
        isOpen={checkoutOpen}
        onClose={() => setCheckoutOpen(false)}
        packageId="critical-mistakes"
        packageName="Критические ошибки"
        price={price}
      />
    </div>
  );
};

export default CriticalMistakesPage;
